import { supabase } from "@/integrations/supabase/client";

// Generate or retrieve a unique session ID for tracking
const getSessionId = (): string => {
  const SESSION_KEY = 'conversion-session-id';
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  
  return sessionId;
};

export type ConversionEventType = 
  | 'whatsapp_click'
  | 'whatsapp_floating_click'
  | 'whatsapp_footer_click'
  | 'whatsapp_contact_click'
  | 'form_submit'
  | 'form_start'
  | 'email_click'
  | 'phone_click'
  | 'cta_click'
  | 'page_view'
  | 'scroll_depth';

// Helper function to track phone calls
export const trackPhoneClick = async (
  language: string,
  sourcePage?: string,
  metadata?: Record<string, unknown>
): Promise<void> => {
  await trackConversionEvent({
    eventType: 'phone_click',
    eventCategory: 'contact',
    language,
    sourcePage,
    metadata: {
      ...metadata,
    },
  });
};

// Helper function to track scroll depth
export const trackScrollDepth = async (
  depth: number,
  language: string,
  sourcePage?: string,
  trafficSource?: string,
  trafficCampaign?: string
): Promise<void> => {
  await trackConversionEvent({
    eventType: 'scroll_depth',
    eventCategory: 'engagement',
    language,
    sourcePage,
    metadata: {
      scroll_depth: depth,
      traffic_source: trafficSource,
      traffic_campaign: trafficCampaign,
    },
  });
};

export type ConversionEventCategory = 
  | 'contact'
  | 'navigation'
  | 'engagement'
  | 'conversion';

interface TrackEventParams {
  eventType: ConversionEventType;
  eventCategory: ConversionEventCategory;
  language?: string;
  sourcePage?: string;
  metadata?: Record<string, any>;
}

export const trackConversionEvent = async ({
  eventType,
  eventCategory,
  language,
  sourcePage,
  metadata,
}: TrackEventParams): Promise<void> => {
  try {
    const sessionId = getSessionId();
    
    const { error } = await supabase
      .from('conversion_analytics')
      .insert({
        event_type: eventType,
        event_category: eventCategory,
        user_session_id: sessionId,
        language: language || navigator.language.split('-')[0],
        source_page: sourcePage || window.location.pathname,
        referrer: document.referrer || 'direct',
        user_agent: navigator.userAgent,
        metadata: metadata || {},
      });

    if (error) {
      console.error('Conversion analytics tracking error:', error);
    }
  } catch (err) {
    // Silently fail - don't disrupt user experience
    console.error('Failed to track conversion analytics:', err);
  }
};

// Helper function to track WhatsApp clicks
export const trackWhatsAppClick = async (
  source: 'floating' | 'footer' | 'contact' | 'inline',
  language: string,
  additionalMetadata?: Record<string, any>
): Promise<void> => {
  const eventTypeMap: Record<typeof source, ConversionEventType> = {
    floating: 'whatsapp_floating_click',
    footer: 'whatsapp_footer_click',
    contact: 'whatsapp_contact_click',
    inline: 'whatsapp_click',
  };

  await trackConversionEvent({
    eventType: eventTypeMap[source],
    eventCategory: 'contact',
    language,
    metadata: {
      source,
      ...additionalMetadata,
    },
  });
};

// Helper function to track form interactions
export const trackFormEvent = async (
  action: 'start' | 'submit',
  formType: string,
  language: string,
  metadata?: Record<string, any>
): Promise<void> => {
  await trackConversionEvent({
    eventType: action === 'start' ? 'form_start' : 'form_submit',
    eventCategory: 'conversion',
    language,
    metadata: {
      formType,
      ...metadata,
    },
  });
};
